import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';

const MODULE_LABELS = {
  case: 'Case study',
  drill: 'Calculation exercise',
  'spot-error': 'Spot-the-error',
  outpatient: 'Outpatient prescription',
  cpd: 'CPD entry',
};

const CPD_TYPE_LABELS = {
  article: 'Article / reading',
  course: 'Course / training',
  audit: 'Audit',
  intervention: 'Clinical intervention',
  other: 'Other',
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB');
    }
    return new Date(value).toLocaleDateString('en-GB');
  } catch {
    return String(value);
  }
};

const formatDateTime = (value) => {
  if (!value) return '';
  try { return new Date(value).toLocaleString('en-GB'); } catch { return String(value); }
};

const entryDateMs = (entry) => {
  const v = entry.occurredAt;
  if (!v) return 0;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return new Date(`${v}T00:00:00`).getTime();
  }
  return new Date(v).getTime();
};

const getTokenFromPath = () => {
  const match = window.location.pathname.match(/^\/portfolio\/share\/([^/?#]+)/);
  return match ? match[1] : '';
};

const PublicPortfolioShareView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorStatus, setErrorStatus] = useState(null);

  const token = useMemo(getTokenFromPath, []);

  const load = useCallback(async () => {
    if (!token) {
      setError('No share link in URL.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const result = await api.getPublicPortfolioShare(token);
      setData(result);
      setError('');
      setErrorStatus(null);
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to load shared portfolio.');
      setErrorStatus(err?.status || null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const visibleEntries = useMemo(() => {
    if (!data) return [];
    const rows = (data.entries || []).slice();
    rows.sort((a, b) => entryDateMs(b) - entryDateMs(a));
    return rows;
  }, [data]);

  const stats = useMemo(() => {
    let cpdHours = 0;
    let reflected = 0;
    const byType = {};
    for (const e of visibleEntries) {
      const t = e.entryKind || e.moduleType;
      byType[t] = (byType[t] || 0) + 1;
      if (e.kind === 'cpd') cpdHours += Number(e.hours) || 0;
      if (e.reflection?.body && e.reflection.body.trim()) reflected += 1;
    }
    return { cpdHours, reflected, byType };
  }, [visibleEntries]);

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  return (
    <div className="portfolio-public-share">
      <style>{`
        .portfolio-public-share {
          background: #f6f7fb;
          min-height: 100vh;
          padding: 24px 0;
        }
        .portfolio-public-share__sheet {
          max-width: 820px;
          margin: 0 auto;
          background: white;
          padding: 32px 40px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          color: #212529;
          font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
          font-size: 13px;
          line-height: 1.4;
        }
        .portfolio-public-share__actions {
          max-width: 820px;
          margin: 0 auto 12px;
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .portfolio-public-share__header {
          border-bottom: 2px solid #212529;
          padding-bottom: 12px;
          margin-bottom: 20px;
        }
        .portfolio-public-share__header h1 {
          font-size: 22px;
          margin: 0 0 4px 0;
        }
        .portfolio-public-share__meta {
          font-size: 11px;
          color: #555;
        }
        .portfolio-public-share__badge {
          display: inline-block;
          background: #084298;
          color: #fff;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 10px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .portfolio-public-share__summary {
          display: flex;
          gap: 24px;
          margin-bottom: 24px;
          flex-wrap: wrap;
          border-bottom: 1px solid #ccc;
          padding-bottom: 16px;
        }
        .portfolio-public-share__summary-item { min-width: 110px; }
        .portfolio-public-share__summary-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #666;
        }
        .portfolio-public-share__summary-value {
          font-size: 20px;
          font-weight: 600;
        }
        .portfolio-public-share__module-counts span {
          display: inline-block;
          background: #f1f3f5;
          padding: 1px 6px;
          margin-right: 4px;
          margin-bottom: 2px;
          border-radius: 8px;
          font-size: 10px;
        }
        .portfolio-public-share__entry {
          page-break-inside: avoid;
          break-inside: avoid;
          margin-bottom: 16px;
          padding: 10px 0;
          border-bottom: 1px solid #e4e6eb;
        }
        .portfolio-public-share__entry:last-child { border-bottom: none; }
        .portfolio-public-share__entry-head {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 12px;
          margin-bottom: 4px;
        }
        .portfolio-public-share__entry-title {
          font-weight: 600;
          font-size: 14px;
          flex: 1;
        }
        .portfolio-public-share__entry-kind {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #444;
          background: #eef1f6;
          padding: 2px 8px;
          border-radius: 10px;
          white-space: nowrap;
        }
        .portfolio-public-share__entry-date {
          font-size: 11px;
          color: #666;
          white-space: nowrap;
        }
        .portfolio-public-share__entry-meta {
          font-size: 11px;
          color: #555;
          margin-bottom: 4px;
        }
        .portfolio-public-share__tags { margin: 4px 0 6px; }
        .portfolio-public-share__tags span {
          display: inline-block;
          background: #e7f1ff;
          color: #084298;
          padding: 1px 6px;
          margin-right: 4px;
          margin-bottom: 2px;
          border-radius: 8px;
          font-size: 10px;
        }
        .portfolio-public-share__notes {
          margin-top: 6px;
          font-size: 12px;
          white-space: pre-wrap;
        }
        .portfolio-public-share__notes-label {
          font-weight: 600;
          font-size: 11px;
          color: #555;
          margin-top: 8px;
        }
        .portfolio-public-share__footer {
          margin-top: 28px;
          padding-top: 12px;
          border-top: 1px solid #ccc;
          font-size: 10px;
          color: #888;
          text-align: center;
        }
        @media print {
          .portfolio-public-share { background: white; padding: 0; }
          .portfolio-public-share__sheet { box-shadow: none; max-width: none; padding: 0; }
          .portfolio-public-share__actions { display: none !important; }
          @page { size: A4; margin: 1.5cm; }
        }
      `}</style>

      <div className="portfolio-public-share__actions">
        <Button variant="primary" size="sm" onClick={handlePrint} disabled={loading || !data}>
          <i className="bi bi-printer me-1" /> Print / Save as PDF
        </Button>
      </div>

      <div className="portfolio-public-share__sheet">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <Alert variant={errorStatus === 410 ? 'warning' : 'danger'}>
            <h5 className="mb-1">
              {errorStatus === 410 ? 'Share link no longer active' : 'Cannot load shared portfolio'}
            </h5>
            <div>{error}</div>
            {errorStatus === 410 ? (
              <div className="small text-muted mt-2">
                The student may have revoked this link or set it to expire. Ask them for a new link.
              </div>
            ) : null}
          </Alert>
        ) : data ? (
          <>
            <div className="portfolio-public-share__header">
              <div className="portfolio-public-share__badge">Shared portfolio</div>
              <h1>{data.portfolio?.name || 'Learning portfolio'}</h1>
              {data.portfolio?.description ? (
                <div className="portfolio-public-share__meta" style={{ marginBottom: 4 }}>
                  {data.portfolio.description}
                </div>
              ) : null}
              <div className="portfolio-public-share__meta">
                {data.student?.displayName ? <>{data.student.displayName} &middot; </> : null}
                Shared {formatDateTime(data.sharedAt)}
              </div>
            </div>

            <div className="portfolio-public-share__summary">
              <div className="portfolio-public-share__summary-item">
                <div className="portfolio-public-share__summary-label">Entries</div>
                <div className="portfolio-public-share__summary-value">{visibleEntries.length}</div>
              </div>
              <div className="portfolio-public-share__summary-item">
                <div className="portfolio-public-share__summary-label">CPD hours</div>
                <div className="portfolio-public-share__summary-value">{stats.cpdHours.toFixed(1)}</div>
              </div>
              <div className="portfolio-public-share__summary-item">
                <div className="portfolio-public-share__summary-label">With reflection</div>
                <div className="portfolio-public-share__summary-value">{stats.reflected}</div>
              </div>
              <div className="portfolio-public-share__summary-item" style={{ flex: 1, minWidth: 220 }}>
                <div className="portfolio-public-share__summary-label">By module</div>
                <div className="portfolio-public-share__module-counts">
                  {Object.entries(stats.byType).length === 0 ? (
                    <span>—</span>
                  ) : (
                    Object.entries(stats.byType)
                      .sort((a, b) => b[1] - a[1])
                      .map(([type, count]) => (
                        <span key={type}>{MODULE_LABELS[type] || type}: {count}</span>
                      ))
                  )}
                </div>
              </div>
            </div>

            {visibleEntries.length === 0 ? (
              <div style={{ fontSize: 12, color: '#666' }}>
                The student hasn&apos;t added any entries to this portfolio yet.
              </div>
            ) : (
              visibleEntries.map((e) => {
                const moduleKind = e.entryKind || e.moduleType;
                const kindLabel = MODULE_LABELS[moduleKind] || moduleKind;
                const tags = Array.isArray(e.tags) ? e.tags : [];
                const comments = Array.isArray(e.lecturerComments) ? e.lecturerComments : [];
                return (
                  <div className="portfolio-public-share__entry" key={`${moduleKind}:${e.entryId}`}>
                    <div className="portfolio-public-share__entry-head">
                      <div className="portfolio-public-share__entry-title">{e.sourceTitle || '(untitled)'}</div>
                      <span className="portfolio-public-share__entry-kind">{kindLabel}</span>
                      <span className="portfolio-public-share__entry-date">{formatDate(e.occurredAt)}</span>
                    </div>
                    <div className="portfolio-public-share__entry-meta">
                      {e.kind === 'cpd' ? (
                        <>
                          {CPD_TYPE_LABELS[e.entryType] || e.entryType}
                          {e.hours ? <> &middot; {Number(e.hours).toFixed(Number.isInteger(Number(e.hours)) ? 0 : 1)} hours</> : null}
                        </>
                      ) : null}
                      {e.kind === 'session' && e.effectiveScore != null ? (
                        <>
                          Score: {Math.round(e.effectiveScore)}%
                          {e.scoreOverride != null ? ' (lecturer-adjusted)' : ''}
                        </>
                      ) : null}
                    </div>
                    {tags.length > 0 ? (
                      <div className="portfolio-public-share__tags">
                        {tags.map((t) => (
                          <span key={t.outcomeItemId}>{t.code} {t.label}</span>
                        ))}
                      </div>
                    ) : null}
                    {e.kind === 'cpd' && e.cpdReflection ? (
                      <>
                        <div className="portfolio-public-share__notes-label">CPD notes</div>
                        <div className="portfolio-public-share__notes">{e.cpdReflection}</div>
                      </>
                    ) : null}
                    {e.reflection?.body ? (
                      <>
                        <div className="portfolio-public-share__notes-label">
                          Reflection
                          {e.reflection.updatedAt ? (
                            <span style={{ fontWeight: 400, color: '#888' }}>
                              {' '}— {formatDateTime(e.reflection.updatedAt)}
                            </span>
                          ) : null}
                        </div>
                        <div className="portfolio-public-share__notes">{e.reflection.body}</div>
                      </>
                    ) : null}
                    {comments.length > 0 ? (
                      <>
                        <div className="portfolio-public-share__notes-label">Lecturer feedback</div>
                        {comments.map((c) => (
                          <div key={c.id} className="portfolio-public-share__notes" style={{ marginTop: 8 }}>
                            <strong>{c.lecturerName || 'Lecturer'}</strong>
                            <span style={{ color: '#888', fontWeight: 400 }}>
                              {' '}— {formatDateTime(c.updatedAt)}
                            </span>
                            <div>{c.body}</div>
                          </div>
                        ))}
                      </>
                    ) : null}
                  </div>
                );
              })
            )}

            <div className="portfolio-public-share__footer">
              Shared via MediCase — read-only view, contents may be revoked by the owner at any time.
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PublicPortfolioShareView;
